// D&D 5e Monster Model
// Adapted from https://github.com/ebshimizu/5e-monster-maker (MIT License)

import { DICE, HD_FOR_SIZE, getModifier, avgRoll, getProficiencyByCr } from './data'

// Utility to generate unique IDs
function generateId() {
  return '_' + (
    Number(String(Math.random()).slice(2)) +
    Date.now() +
    Math.round(performance.now())
  ).toString(36)
}

// Default save factory
export function defaultSave() {
  return {
    proficient: false,
    override: false,
    overrideValue: 0,
  }
}

// Default skill factory
export function defaultSkill() {
  return {
    proficient: false,
    expertise: false,
    override: false,
    overrideValue: 0,
    note: '',
  }
}

// Default speed factory
export function defaultSpeed(type = 'walk') {
  return {
    id: generateId(),
    type: type,
    speed: type === 'walk' ? 30 : 0,
    note: '',
  }
}

// Default attack factory
export function defaultAttack() {
  return {
    id: generateId(),
    name: 'New Attack',
    attackType: 'Melee',
    distance: 'REACH',
    reach: 5,
    range: 0,
    longRange: 0,
    modifier: {
      override: false,
      overrideValue: 0,
      stat: 'STR',
      proficient: true,
    },
    damage: {
      count: 1,
      die: DICE.d6,
      modifier: {
        override: false,
        overrideValue: 0,
        stat: 'STR',
      },
      type: 'Slashing',
    },
    alternateDamage: [],
    additionalDamage: [],
    traits: '',
    description: '',
  }
}

// Default trait factory
export function defaultTrait() {
  return {
    id: generateId(),
    name: 'New Trait',
    description: '',
    crpiledDamageEnabled: false,
    crpiledDamage: { count: 0, die: DICE.d6, type: '' },
  }
}

// Default action factory
export function defaultAction() {
  return {
    id: generateId(),
    name: 'New Action',
    description: '',
    recharge: '',
    limitedUse: {
      count: 0,
      rate: '',
    },
    crpiledDamage: { count: 0, die: DICE.d6, type: '' },
    crpiledDamageEnabled: false,
    legendaryOnly: false,
    bonusAction: false,
  }
}

// Default reaction factory
export function defaultReaction() {
  return {
    id: generateId(),
    name: 'New Reaction',
    description: '',
    recharge: '',
    limitedUse: {
      count: 0,
      rate: '',
    },
  }
}

// Default bonus action factory
export function defaultBonusAction() {
  return {
    id: generateId(),
    name: 'New Bonus Action',
    description: '',
    recharge: '',
  }
}

// Default legendary action factory
export function defaultLegendaryAction() {
  return {
    id: generateId(),
    name: '',
    description: '',
    actionId: null,
  }
}

// Default spellcasting configuration
export function defaultSpellcasting() {
  return {
    stat: 'INT',
    save: {
      override: false,
      overrideValue: 0,
    },
    attack: {
      override: false,
      overrideValue: 0,
    },
    level: 1,
    slots: [0, 0, 0, 0, 0, 0, 0, 0, 0], // Slots for levels 1-9
    class: '',
    preamble: '',
    standard: {
      preambleAdditions: [],
    },
    atWill: {
      spells: [],
    },
    knownSpells: [],
  }
}

// Main monster factory function
export function createMonster() {
  return {
    id: generateId(),
    name: '',
    nickname: '',
    size: ['Medium'],
    type: ['Humanoid'],
    customType: '',
    descriptiveTag: '',
    alignment: 'Neutral',
    swarm: false,
    swarmSize: 'Medium',

    // Combat stats
    AC: 10,
    ACNote: '',
    initiative: {
      override: false,
      overrideValue: 0,
    },
    CR: '1',
    hasLair: false,
    proficiency: 2,
    proficiencyOverride: false,

    // Hit points
    HP: {
      HD: 1,
      type: DICE.d8,
      modifier: 0,
    },
    hpModifierOverride: false,

    // Ability scores
    stats: {
      STR: 10,
      DEX: 10,
      CON: 10,
      INT: 10,
      WIS: 10,
      CHA: 10,
    },

    // Saving throws
    saves: {
      STR: defaultSave(),
      DEX: defaultSave(),
      CON: defaultSave(),
      INT: defaultSave(),
      WIS: defaultSave(),
      CHA: defaultSave(),
    },

    // Skills
    skills: {
      ACROBATICS: defaultSkill(),
      ANIMAL_HANDLING: defaultSkill(),
      ARCANA: defaultSkill(),
      ATHLETICS: defaultSkill(),
      DECEPTION: defaultSkill(),
      HISTORY: defaultSkill(),
      INSIGHT: defaultSkill(),
      INTIMIDATION: defaultSkill(),
      INVESTIGATION: defaultSkill(),
      MEDICINE: defaultSkill(),
      NATURE: defaultSkill(),
      PERCEPTION: defaultSkill(),
      PERFORMANCE: defaultSkill(),
      PERSUASION: defaultSkill(),
      RELIGION: defaultSkill(),
      SLEIGHT_OF_HAND: defaultSkill(),
      STEALTH: defaultSkill(),
      SURVIVAL: defaultSkill(),
    },

    // Movement
    speeds: [defaultSpeed('walk')],

    // Senses
    spiledPerceptionOverride: false,
    spiledPerception: 10,
    gear: '',
    blindsight: 0,
    blindsightNote: '',
    darkvision: 0,
    tremorsense: 0,
    truesight: 0,

    // Resistances, immunities, vulnerabilities
    resistances: [],
    immunities: [],
    vulnerabilities: [],
    conditionImmunities: [],

    // Languages
    languages: '',
    telepathy: 0,

    // Combat features
    attacks: [],
    multiattack: '',
    multiattacks: [],
    actions: [],
    bonusActions: [],
    traits: [],
    reactions: [],
    legendaryActions: {
      count: 0,
      actions: [],
      preamble: '',
      preambleEdited: false,
    },
    mythicActions: {
      triggerName: '',
      triggerDescription: '',
      preamble: '',
      actions: [],
    },
    lairActions: {
      preamble: '',
      actions: [],
      regionalEffects: [],
      regionalEffectDescription: '',
    },

    // Spellcasting
    useInnateSpellcasting: false,
    useClassSpellcasting: false,
    spellcasting: defaultSpellcasting(),

    // Inventory
    inventory: [],

    // Description
    description: '',
    artdirection: '',
    source: '',
    wordCount: 0,
  }
}

// Computed properties for monster
export function getMonsterComputedProps(monster) {
  const proficiency = monster.proficiencyOverride
    ? monster.proficiency
    : getProficiencyByCr(monster.CR)

  const statsWithModifiers = {}
  for (const stat of Object.keys(monster.stats)) {
    statsWithModifiers[stat] = {
      score: monster.stats[stat],
      modifier: getModifier(monster.stats[stat]),
    }
  }

  const avgHp = avgRoll(monster.HP.HD, monster.HP.type) + monster.HP.modifier

  const passivePerception = monster.spiledPerceptionOverride
    ? monster.spiledPerception
    : 10 + getModifier(monster.stats.WIS) +
      (monster.skills.PERCEPTION.proficient ? proficiency : 0) +
      (monster.skills.PERCEPTION.expertise ? proficiency : 0)

  return {
    proficiency,
    statsWithModifiers,
    avgHp,
    passivePerception,
  }
}

// Set CR and optionally auto-calculate related values
export function setMonsterCr(monster, cr, autoCalculate = false) {
  monster.CR = cr
  if (!monster.proficiencyOverride) {
    monster.proficiency = getProficiencyByCr(cr)
  }
}
